/**
 * Question Banks page — list/detail orchestration shell (decomposed Phase 5).
 *
 * Phase A (May 17, 2026): kebab-menu actions (Edit / Duplicate / Retire /
 *   Delete) on cards + detail header, wired to PUT, POST /duplicate,
 *   PATCH /lifecycle, DELETE (super-admin `?force=true`).
 * Phase B: cohesive --lumyn-banks theming.
 * Phase C: search (debounced), status filter chips, sort.
 *
 * List view → BankList; single-bank view → BankDetail; this shell owns the
 * bank-level CRUD actions + dialogs shared by both.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { SkeletonCard } from '../components/common/SkeletonCard';
import CreateBankDialog from '../components/banks/CreateBankDialog';
import EditBankDialog from '../components/banks/EditBankDialog';
import { BankList } from '../components/banks/BankList';
import { BankDetail } from '../components/banks/BankDetail';

export default function QuestionBanksPage() {
  const { get, post, patch, del } = useApi();
  const { user, hasRole } = useAuth();
  // Trainers see banks read-only (Feb 2026 split). Editor+ get full CRUD.
  const canEdit = hasRole('editor');
  const params = useParams();
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBank, setActiveBank] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTargetBank, setEditTargetBank] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const loadBanks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get('/question-banks?limit=200');
      setBanks(res.banks || []);
      if (params.bankId) {
        const found = (res.banks || []).find(b => b.bank_id === params.bankId);
        if (found) setActiveBank(found);
      }
    } catch { toast.error('Failed to load banks'); }
    finally { setLoading(false); }
  }, [get, params.bankId]);

  useEffect(() => { loadBanks(); }, [loadBanks]);

  const handleCreated = async (created) => {
    await loadBanks();
    navigate(`/editor/banks/${created.bank_id}`);
    setActiveBank(created);
  };

  const handleOpen = (b) => { navigate(`/editor/banks/${b.bank_id}`); setActiveBank(b); };
  const handleBack = () => { navigate('/editor/banks'); setActiveBank(null); };

  // ── Per-bank actions (used by both list and detail) ──
  const handleEdit = (b) => setEditTargetBank(b);

  const handleEditSaved = async (updated) => {
    await loadBanks();
    if (activeBank && activeBank.bank_id === updated.bank_id) {
      setActiveBank(updated);
    }
  };

  const handleDuplicate = async (b) => {
    try {
      const dup = await post(`/question-banks/${b.bank_id}/duplicate`, {});
      toast.success(`Duplicated → ${dup.title}`);
      await loadBanks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    }
  };

  const handleRetire = async (b) => {
    if (b.lifecycle_status === 'retired') return;
    if (!window.confirm(`Retire "${b.title}"? Items remain readable; new attempts won't sample from it.`)) return;
    try {
      const updated = await patch(`/question-banks/${b.bank_id}/lifecycle`, { target_status: 'retired' });
      toast.success('Bank retired');
      await loadBanks();
      if (activeBank?.bank_id === b.bank_id) {
        setActiveBank(updated);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retire failed');
    }
  };

  const handleDeleteRequest = (b) => setDeleteTarget(b);

  const handleDeleteConfirm = async (force = false) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const qs = force ? '?force=true' : '';
      await del(`/question-banks/${deleteTarget.bank_id}${qs}`);
      toast.success(`Deleted "${deleteTarget.title}"`);
      // If we deleted the bank currently being viewed, bounce back to the list
      if (activeBank?.bank_id === deleteTarget.bank_id) {
        navigate('/editor/banks');
        setActiveBank(null);
      }
      setDeleteTarget(null);
      await loadBanks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <SkeletonCard />;

  const view = activeBank ? (
    <BankDetail
      bank={activeBank}
      onBack={handleBack}
      onChanged={loadBanks}
      onEdit={handleEdit}
      onDuplicate={handleDuplicate}
      onRetire={handleRetire}
      onDelete={handleDeleteRequest}
      canEdit={canEdit}
    />
  ) : (
    <BankList
      banks={banks}
      onCreate={canEdit ? (() => setCreateOpen(true)) : null}
      onOpen={handleOpen}
      onEdit={canEdit ? handleEdit : null}
      onDuplicate={canEdit ? handleDuplicate : null}
      onRetire={canEdit ? handleRetire : null}
      onDelete={canEdit ? handleDeleteRequest : null}
      canEdit={canEdit}
    />
  );

  // Decide which Delete button to render. Heuristic: if the bank has any items
  // at all (item_count includes retired), the standard delete will *probably*
  // be blocked by the backend's "live items" check. Super-admins get an
  // explicit "Force delete" button that bypasses the check. The backend is
  // still the source of truth — non-super-admin clicks fall through with a
  // toast carrying the precise reason if the bank happens to have only
  // retired items (rare) and the standard delete actually succeeds.
  const target = deleteTarget;
  const showForceButton = target && isSuperAdmin && (target.item_count || 0) > 0;

  return (
    <>
      {view}
      <CreateBankDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
      <EditBankDialog
        open={!!editTargetBank}
        onClose={() => setEditTargetBank(null)}
        bank={editTargetBank}
        onSaved={handleEditSaved}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="bank-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{target?.title}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the bank and all its item versions.
              Historical quiz attempts that already snapshotted these items remain valid and viewable.
              {target && (target.item_count || 0) > 0 && !isSuperAdmin && (
                <span className="block mt-2 text-amber-700 dark:text-amber-400 text-xs">
                  Heads up: this bank has {target.item_count} item(s). If any are still live (not retired), the delete will be blocked — retire them first, or ask a super-admin.
                </span>
              )}
              {target && (target.item_count || 0) > 0 && isSuperAdmin && (
                <span className="block mt-2 text-amber-700 dark:text-amber-400 text-xs">
                  This bank has {target.item_count} item(s). &ldquo;Force delete&rdquo; removes them all — irreversible.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="bank-delete-cancel">Cancel</AlertDialogCancel>
            {showForceButton ? (
              <AlertDialogAction
                onClick={() => handleDeleteConfirm(true)}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="bank-delete-force"
              >
                {deleting ? 'Deleting…' : 'Force delete'}
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={() => handleDeleteConfirm(false)}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="bank-delete-confirm"
              >
                {deleting ? 'Deleting…' : 'Delete bank'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
